import React, { useState, useEffect, createContext, useContext, Component } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { ErrorBoundary } from 'react-error-boundary';
import './App.css';

// Context
import { AuthProvider, useAuth } from './context/AuthContext';
import { AudienceProvider, useAudience } from './context/AudienceContext';
import { SubscriptionProvider, useSubscription } from './context/SubscriptionContext';

// Error Handling
import ErrorFallback from './components/ErrorFallback';

// Auth Components
import AuthCallback from './components/AuthCallback';

// Pages
import DashboardPage from './pages/DashboardPage';
import MedicationsPage from './pages/MedicationsPage';
import MedicationDetailPage from './pages/MedicationDetailPage';
import DoseCalculatorPage from './pages/DoseCalculatorPage';
import ScoresPage from './pages/ScoresPage';
import ProtocolsPage from './pages/ProtocolsPage';
import ProtocolDetailPage from './pages/ProtocolDetailPage';
import ChatPage from './pages/ChatPage';
import AlertsPage from './pages/AlertsPage';
import AntibioPage from './pages/AntibioPage';
import DiagnosticPage from './pages/DiagnosticPage';
import EruptiveDiseasesPage from './pages/EruptiveDiseasesPage';
import AVCExpertPage from './pages/AVCExpertPage';
import IntubationPage from './pages/IntubationPage';
import TraumaSeverePage from './pages/TraumaSeverePage';
import IntubationPediatricPage from './pages/IntubationPediatricPage';
import MARCHEChecklistPage from './pages/MARCHEChecklistPage';
import AccouchementAidePage from './pages/AccouchementAidePage';
import VoieIntraosseusePage from './pages/VoieIntraosseusePage';
import TraumatoUrgencesPage from './pages/TraumatoUrgencesPage';
import SCASTPage from './pages/SCASTPage';
import ECGAIPage from './pages/ECGAIPage';
import SMURPage from './pages/SMURPage';
import PricingPage from './pages/PricingPage';
import SubscriptionSuccessPage from './pages/SubscriptionSuccessPage';
import NSTEMIPage from './pages/NSTEMIPage';
import EESPage from './pages/EESPage';
import VNIPage from './pages/VNIPage';
import VMPage from './pages/VMPage';
import VoieIntraNasalePage from './pages/VoieIntraNasalePage';
import SimpleLoginPage from './pages/SimpleLoginPage';
import NewLoginPage from './pages/NewLoginPage';
import SedationProceduralePage from './pages/SedationProceduralePage';
import RamsayScorePage from './pages/RamsayScorePage';
import DrainageThoraciquePage from './pages/DrainageThoraciquePage';
import ModesVentilatoiresPage from './pages/ModesVentilatoiresPage';
import TamponnadePage from './pages/TamponnadePage';
import ParametresEnfantPage from './pages/ParametresEnfantPage';
import PonctionPericardiquePage from './pages/PonctionPericardiquePage';
import NoradrenalinePage from './pages/NoradrenalinePage';
import LactateSodiumPage from './pages/LactateSodiumPage';
import ProfilePage from './pages/ProfilePage';
import PrescriptionAdultePage from './pages/PrescriptionAdultePage';
import PrescriptionPediatriePage from './pages/PrescriptionPediatriePage';
import InfectionsPage from './pages/InfectionsPage';
import GazDuSangPage from './pages/GazDuSangPage';
import LCRPage from './pages/LCRPage';
import PonctionLombairePage from './pages/PonctionLombairePage';
import AdminPage from './pages/AdminPage';
import CGUPage from './pages/CGUPage';
import PrivacyPage from './pages/PrivacyPage';
import GuidePage from './pages/GuidePage';
import ReferentielsCardioPage from './pages/ReferentielsCardioPage';
import ReferentielsCirconstancielPage from './pages/ReferentielsCirconstancielPage';
import ReferentielsPediatrieNeontalogiePage from './pages/ReferentielsPediatrieNeontalogiePage';
import ReferentielsNeuroPage from './pages/ReferentielsNeuroPage';
import ReferentielsIntoxPage from './pages/ReferentielsIntoxPage';
import ReferentielsMetaboliquePage from './pages/ReferentielsMetaboliquePage';
import ReferentielsTraumaPage from './pages/ReferentielsTraumaPage';
import ReferentielsInfectiologiePage from './pages/ReferentielsInfectiologiePage';
import ReferentielsPneumologiePage from './pages/ReferentielsPneumologiePage';
import ReferentielsGastroEPage from './pages/ReferentielsGastroEPage';
import UrgRythmologiePage from './pages/UrgRythmologiePage';
import MedicamentsAdultePage from './pages/MedicamentsAdultePage';
import MedicamentsPediatriePage from './pages/MedicamentsPediatriePage';

// Components
import Layout from './components/Layout';

// Protected Route Component
// Protected Route Component - Vérifie uniquement l'authentification
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[hsl(var(--bg))] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-cyan-400 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-cyan-300">Chargement...</p>
        </div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
}

// Wrapper for protected layout pages with subscription check
function ProtectedPage({ children }) {
  const { hasAccess, loading: subLoading, isAdmin } = useSubscription();
  const { loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Si loading auth ou subscription, afficher le spinner
  if (authLoading || subLoading) {
    return (
      <div className="min-h-screen bg-[hsl(var(--bg))] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-cyan-400 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-cyan-300">Vérification de l&apos;abonnement...</p>
        </div>
      </div>
    );
  }

  // Si pas d'accès et pas admin, rediriger vers subscription
  if (!hasAccess && !isAdmin && location.pathname !== '/subscription') {
    return <Navigate to="/subscription" replace />;
  }

  return (
    <ProtectedRoute>
      <Layout>{children}</Layout>
    </ProtectedRoute>
  );
}

function App() {
  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <Router>
        <AuthProvider>
          <SubscriptionProvider>
            <AudienceProvider>
              <div className="app-shell scanlines">
                <Routes>
                  {/* ACCÈS LIBRE - Toutes les pages accessibles sans authentification */}
                  <Route path="/" element={<Layout><DashboardPage /></Layout>} />
                  <Route path="/medications" element={<Layout><MedicationsPage /></Layout>} />
                  <Route path="/medications/:id" element={<Layout><MedicationDetailPage /></Layout>} />
                  <Route path="/calculator" element={<Layout><DoseCalculatorPage /></Layout>} />
                  <Route path="/scores" element={<Layout><ScoresPage /></Layout>} />
                  <Route path="/ramsay-score" element={<Layout><RamsayScorePage /></Layout>} />
                  <Route path="/protocols" element={<Layout><ProtocolsPage /></Layout>} />
                  <Route path="/protocols/:id" element={<Layout><ProtocolDetailPage /></Layout>} />
                  <Route path="/chat" element={<Layout><ChatPage /></Layout>} />
                  <Route path="/alerts" element={<Layout><AlertsPage /></Layout>} />
                  <Route path="/antibio" element={<Layout><AntibioPage /></Layout>} />
                  <Route path="/diagnostic" element={<Layout><DiagnosticPage /></Layout>} />
                  <Route path="/eruptive-diseases" element={<Layout><EruptiveDiseasesPage /></Layout>} />
                  <Route path="/avc-expert" element={<Layout><AVCExpertPage /></Layout>} />
                  <Route path="/sca-st-plus" element={<Layout><SCASTPage /></Layout>} />
                  <Route path="/ecg-ai" element={<Layout><ECGAIPage /></Layout>} />
                  <Route path="/smur" element={<Layout><SMURPage /></Layout>} />
                  <Route path="/nstemi" element={<Layout><NSTEMIPage /></Layout>} />
                  <Route path="/ees" element={<Layout><EESPage /></Layout>} />
                  <Route path="/vni" element={<Layout><VNIPage /></Layout>} />
                  <Route path="/vm" element={<Layout><VMPage /></Layout>} />
                  <Route path="/modes-ventilatoires" element={<Layout><ModesVentilatoiresPage /></Layout>} />
                  <Route path="/tamponnade" element={<Layout><TamponnadePage /></Layout>} />
                  <Route path="/ponction-pericardique" element={<Layout><PonctionPericardiquePage /></Layout>} />
                  <Route path="/parametres-enfant" element={<Layout><ParametresEnfantPage /></Layout>} />
                  <Route path="/noradrenaline" element={<Layout><NoradrenalinePage /></Layout>} />
                  <Route path="/lactate-sodium" element={<Layout><LactateSodiumPage /></Layout>} />
                  <Route path="/voie-nasale" element={<Layout><VoieIntraNasalePage /></Layout>} />
                  <Route path="/profile" element={<Layout><ProfilePage /></Layout>} />
                  <Route path="/prescription-adulte" element={<Layout><PrescriptionAdultePage /></Layout>} />
                  <Route path="/prescription-pediatrie" element={<Layout><PrescriptionPediatriePage /></Layout>} />
                  <Route path="/infections" element={<Layout><InfectionsPage /></Layout>} />
                  <Route path="/gaz-du-sang" element={<Layout><GazDuSangPage /></Layout>} />
                  <Route path="/lcr" element={<Layout><LCRPage /></Layout>} />
                  <Route path="/ponction-lombaire" element={<Layout><PonctionLombairePage /></Layout>} />
                  <Route path="/admin" element={<Layout><AdminPage /></Layout>} />
                  <Route path="/cgu" element={<Layout><CGUPage /></Layout>} />
                  <Route path="/privacy" element={<Layout><PrivacyPage /></Layout>} />
                  <Route path="/guide" element={<Layout><GuidePage /></Layout>} />
                  <Route path="/intubation" element={<Layout><IntubationPage /></Layout>} />
                  <Route path="/intubation-pediatric" element={<Layout><IntubationPediatricPage /></Layout>} />
                  <Route path="/sedation-procedurale" element={<Layout><SedationProceduralePage /></Layout>} />
                  <Route path="/drainage-thoracique" element={<Layout><DrainageThoraciquePage /></Layout>} />
                  <Route path="/trauma-severe" element={<Layout><TraumaSeverePage /></Layout>} />
                  <Route path="/marche-checklist" element={<Layout><MARCHEChecklistPage /></Layout>} />
                  <Route path="/accouchement-aide" element={<Layout><AccouchementAidePage /></Layout>} />
                  <Route path="/voie-intraosseuse" element={<Layout><VoieIntraosseusePage /></Layout>} />
                  <Route path="/traumato-urgences" element={<Layout><TraumatoUrgencesPage /></Layout>} />
                  <Route path="/cardio-ref" element={<Layout><ReferentielsCardioPage /></Layout>} />
                  <Route path="/circonstanciel-ref" element={<Layout><ReferentielsCirconstancielPage /></Layout>} />
                  <Route path="/pediatrie-ref" element={<Layout><ReferentielsPediatrieNeontalogiePage /></Layout>} />
                  <Route path="/neuro-ref" element={<Layout><ReferentielsNeuroPage /></Layout>} />
                  <Route path="/intox-ref" element={<Layout><ReferentielsIntoxPage /></Layout>} />
                  <Route path="/metabolique-ref" element={<Layout><ReferentielsMetaboliquePage /></Layout>} />
                  <Route path="/trauma-ref" element={<Layout><ReferentielsTraumaPage /></Layout>} />
                  <Route path="/infectio-ref" element={<Layout><ReferentielsInfectiologiePage /></Layout>} />
                  <Route path="/pneumo-ref" element={<Layout><ReferentielsPneumologiePage /></Layout>} />
                  <Route path="/gastro-ref" element={<Layout><ReferentielsGastroEPage /></Layout>} />
                  <Route path="/urg-rythmo" element={<Layout><UrgRythmologiePage /></Layout>} />
                  <Route path="/medicaments-adulte" element={<Layout><MedicamentsAdultePage /></Layout>} />
                  <Route path="/medicaments-pediatrie" element={<Layout><MedicamentsPediatriePage /></Layout>} />
                  
                  {/* Fallback */}
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
            </div>
          </AudienceProvider>
        </SubscriptionProvider>
      </AuthProvider>
    </Router>
  </ErrorBoundary>
  );
}

export default App;
